import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  InputSignal,
  model,
  ModelSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isFileExistInArray } from '../../../utils/file.util';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrl: './gallery.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GalleryComponent),
      multi: true
    }
  ],
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GalleryComponent implements ControlValueAccessor {
  files: ModelSignal<File[]> = model<File[]>([]);

  uploadCustomFileLabel: InputSignal<string> = input<string>('Upload your own');
  isUploadCustomFileAvailable: InputSignal<boolean> = input<boolean>(true);

  // State
  fileImages: Signal<string[]> = computed((): string[] => {
    const files: File[] = this.files();
    return files.map((file: File): string => {
      return URL.createObjectURL(file);
    });
  });

  selectedFileIndex: WritableSignal<number> = signal<number>(-1);

  onUploadCustomFile(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | undefined = target.files?.[0];
    if (!file) {
      return;
    }

    const isFileExist: boolean = isFileExistInArray(file, this.files());
    if (isFileExist) {
      return;
    }

    this.files.update((files: File[]): File[] => [...files, file]);

    const fileIndex: number = this.files().length - 1;
    this.selectedFileIndex.set(fileIndex);
    this.onChange?.(this.files()[fileIndex]);
    this.onTouched?.();
  }

  onSelectFile(fileIndex: number): void {
    this.selectedFileIndex.set(fileIndex);
    this.onChange?.(this.files()[fileIndex]);
    this.onTouched?.();
  }

  // ControlValueAccessor functions
  private onChange?: (value: File) => void;
  private onTouched?: () => void;

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(file: File | null): void {
    if (!file) {
      this.selectedFileIndex.set(-1);
      return;
    }

    let selectedFileIndex: number = this.getFileIndexByName(file.name);
    if (selectedFileIndex === -1) {
      this.files.update((files: File[]): File[] => [...files, file]);
      selectedFileIndex = this.files().length - 1;
    }

    this.selectedFileIndex.set(selectedFileIndex);
  }

  private getFileIndexByName(
    fileName: string,
    files: File[] = this.files()
  ): number {
    return files.findIndex((file: File): boolean => {
      return file.name == fileName;
    });
  }
}
